// Cisco technology footprint catalog. Edit here to add or rename a tech
// domain; no scoring changes required. Weights live in ./weights.ts (every
// new tech domain needs an entry there). `pnpm validate:taxonomy` cross-checks
// both files. Internal type/const names (UseCaseId, UseCaseDef, USE_CASES)
// are kept from the upstream engine even though this catalog is no longer
// about "AI use cases" — it's the same adaptive-filtering mechanism
// repurposed for "which infrastructure do you run".

import type { UseCaseId } from "../shared/types";

export type UseCaseDef = {
  id: UseCaseId;
  name: string;
  description: string;
  icon: string;
  longDescription?: string;
  examples?: string[];
  // Free-text capture for the "Other" option only.
  allowCustomDescription?: boolean;
  // Surfaced as a footnote in reports for non-standard footprints.
  customProfileNote?: string;
  // Catalog membership toggle. Absent/undefined => shown; only `false` hides
  // it from the picker (still resolvable by id, re-enableable in admin).
  enabled?: boolean;
};

export const USE_CASES: UseCaseDef[] = [
  { id: "nexus_dc", name: "Nexus Data Center Switching",
    description: "Cisco Nexus switches in the data center — EOL/LDoS exposure and lateral-movement risk if under-segmented.",
    icon: "Server",
    longDescription: "Cisco Nexus switching in the data center fabric. Because this infrastructure sits at the core of east-west traffic, unpatched or end-of-life Nexus gear is both a direct exploitation target and a lateral-movement enabler if segmentation is weak.",
    examples: [
      "Nexus 9000 series data center switching",
      "Nexus Dashboard-managed fabric",
      "ACI-based data center fabric",
    ] },
  { id: "meraki", name: "Meraki",
    description: "Cloud-managed switching, wireless, and security appliances — visibility is strong, but patch/config discipline still matters.",
    icon: "Cloud",
    longDescription: "Cisco Meraki cloud-managed switches, access points, and security appliances. Centralized cloud management simplifies visibility and configuration, but firmware currency, dashboard access control, and network segmentation still need active management.",
    examples: [
      "Meraki MS switches",
      "Meraki MR access points",
      "Meraki MX security appliances",
    ] },
  { id: "catalyst_sdwan", name: "Catalyst SD-WAN",
    description: "SD-WAN fabric and controllers connecting sites — control-plane exposure and orchestrator hardening matter most.",
    icon: "Network",
    longDescription: "Cisco Catalyst SD-WAN (vManage/vSmart/vBond and edge routers) connecting distributed sites. The control plane and orchestration layer are high-value targets — compromising the controller can affect every connected site, so hardening and access control on the orchestrator matter as much as the edge devices.",
    examples: [
      "Catalyst SD-WAN Manager (vManage)",
      "SD-WAN edge routers at branch sites",
      "Cisco SD-WAN Controller infrastructure",
    ] },
  { id: "catalyst_switching", name: "Catalyst Switching",
    description: "Campus/branch access and core switching — often the largest EOL population in an estate.",
    icon: "Cable",
    longDescription: "Cisco Catalyst campus and branch switching. This is frequently the largest single population of aging infrastructure in an estate, and access-layer switches are the first hop an attacker who's gained a foothold on an endpoint will reach.",
    examples: [
      "Catalyst 9000 series access switching",
      "Catalyst Center-managed campus fabric",
      "Branch-office access switching",
    ] },
  { id: "cisco_firewall", name: "Cisco Firewalls",
    description: "Secure Firewall / ASA perimeter and segmentation enforcement points.",
    icon: "ShieldCheck",
    longDescription: "Cisco Secure Firewall (Firepower) and ASA devices enforcing perimeter and internal segmentation policy. Rule sprawl, unpatched firmware, and exposed management interfaces on these devices directly translate into exploitable perimeter and lateral-movement risk.",
    examples: [
      "Secure Firewall Threat Defense (FTD)",
      "ASA-based perimeter firewalls",
      "Firewall Management Center (FMC)",
    ] },
  { id: "collaboration", name: "Collaboration",
    description: "Voice, video, and messaging platforms — a growing target for AI-generated phishing and vishing.",
    icon: "MessagesSquare",
    longDescription: "Collaboration infrastructure — calling, video, and messaging. AI-generated deepfake audio/video and highly convincing vishing/phishing increasingly target these channels directly, making identity verification and user awareness around collaboration tools a distinct risk surface.",
    examples: [
      "Webex Calling and Meetings",
      "Unified Communications Manager (CUCM)",
      "Contact center / IVR platforms",
    ] },
  { id: "wifi", name: "Enterprise Wireless (non-Meraki)",
    description: "Catalyst 9800 / Aironet wireless infrastructure — rogue AP and unmanaged wireless access risk.",
    icon: "Wifi",
    longDescription: "Enterprise wireless infrastructure managed outside of Meraki (e.g., Catalyst 9800 wireless controllers, Aironet access points). Rogue or misconfigured access points and weak wireless authentication are common entry points that bypass wired-network segmentation.",
    examples: [
      "Catalyst 9800 wireless LAN controllers",
      "Aironet access points",
      "Guest and BYOD wireless networks",
    ] },

  // Free-text "Other / non-Cisco infrastructure" — scored with a conservative
  // default profile, same role as the legacy "other_custom" catch-all.
  { id: "other_non_cisco", name: "Other / Non-Cisco Infrastructure",
    description: "Infrastructure from other vendors not listed above. We'll apply a conservative generic risk profile and flag this in the report.",
    icon: "Plus",
    longDescription: "Infrastructure from vendors not captured above (e.g., Juniper, Palo Alto, Arista, F5). We apply a conservative, generic risk profile and flag it in the report so multi-vendor exposure is still represented rather than silently dropped.",
    examples: [
      "Non-Cisco routing or switching",
      "Third-party next-gen firewalls",
      "Other-vendor wireless or SD-WAN",
    ],
    allowCustomDescription: true,
    customProfileNote: "Non-Cisco infrastructure — generic risk profile applied. Review threat exposures with extra care; specific tuning may require adding a dedicated weights row.",
  },
];

export function getUseCase(id: UseCaseId): UseCaseDef | undefined {
  return USE_CASES.find((u) => u.id === id);
}
